import { useEffect, useRef, useState } from "react";
import { Form, Row, Col, Button, ButtonGroup } from "react-bootstrap";
import styles from "./UnitPriceForm.module.css";
import { FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";
import UnitPriceConfirmation from "./UnitPriceConfirmation";
import { isDefaultRow } from "../../../utils/unitPrice";
import FieldInfo from "../MarketPlaceDetail/FieldInfo";
import { FIELD_INFO } from "../MarketPlaceDetail/fieldInfoContent";
import ActiveSubscriptionsBadge from "./ActiveSubscriptionsBadge";

const UnitPriceForm = ({
  allow_delete,
  unitPrices,
  onSave,
  adminVendorId,
  productVendorId,
  inline = false,
  priceId,
}) => {
  const [showCard, setShowCard] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [unitPriceErrors, setUnitPriceErrors] = useState({});
  // FIX 4: snapshot the plan's ORIGINAL unit prices ONCE at first render (the DB
  // values, before any live-commit edit mutates the `unitPrices` prop). The
  // "changed" detection diffs against this instead of the last live-committed
  // value, so an edit-then-revert-to-original correctly clears the changed flag
  // and no longer over-fires the active-subscription re-price warning.
  const originalUnitPricesRef = useRef(null);
  if (originalUnitPricesRef.current === null) {
    originalUnitPricesRef.current = (unitPrices || []).map((u) => ({ ...u }));
  }
  const [unitPriceForms, setUnitPriceForms] = useState([
    {
      id: String(Date.now()) + "-" + Math.random().toString(36).slice(2),
      type: "new",
      StartRange: "0",
      EndRange: "",
      Steps: "1",
      Marketplace_price: "",
      Industry_price: "",
      Vendor_price: "",
      Min_price: "",
      Max_price: "",
      hsn_code: "",
      DefaultValue: true,
      changed_unitprice: false,
    },
  ]);

  // In inline mode there's no open/close toggle — hydrate from unitPrices on
  // mount and whenever they change so the sub-stepper mirrors the plan's tiers.
  useEffect(() => {
    if (unitPrices && unitPrices.length) {
      const withIds = unitPrices.map((u, i) => ({
        ...u,
        id: u.id
          ? u.id
          : String(Date.now()) + "-" + Math.random().toString(36).slice(2),
        type: "existing",
        changed_unitprice: u.changed_unitprice ?? false,
      }));
      setUnitPriceForms(withIds);
    }
  }, [showCard, unitPrices]);

  const addNewForm = () => {
    const newForm = {
      id: String(Date.now()) + "-" + Math.random().toString(36).slice(2),
      type: "new",
      StartRange: "",
      EndRange: "",
      Steps: "1",
      Marketplace_price: "",
      Industry_price: "",
      Vendor_price: "",
      Min_price: "",
      Max_price: "",
      hsn_code: "",
      DefaultValue: false,
      changed_unitprice: false,
    };
    const next = [...unitPriceForms, newForm];
    setUnitPriceForms(next);
    commitInline(next);
  };

  const validateUnitPriceForms = (forms) => {
    const errors = {};
    let isValid = true;
    let hasRangeIssue = false;

    forms.forEach((form, index) => {
      const rowErrors = {};
      const requiredFields = [
        "StartRange",
        "EndRange",
        "Marketplace_price",
        "Industry_price",
        "Vendor_price",
        "hsn_code",
      ];
      requiredFields.forEach((field) => {
        const value = form[field];
        if (value === "" || value === null || value === undefined) {
          rowErrors[field] = true;
          isValid = false;
        }
      });
      // EndRange must exceed StartRange (unit ranges are inclusive [start, end];
      // the billing matcher selects the tier where qty is between them).
      const s = Number(form.StartRange);
      const e = Number(form.EndRange);
      if (
        form.StartRange !== "" &&
        form.EndRange !== "" &&
        Number.isFinite(s) &&
        Number.isFinite(e) &&
        e <= s
      ) {
        rowErrors.EndRange = true;
        isValid = false;
        hasRangeIssue = true;
      }
      if (Object.keys(rowErrors).length > 0) {
        errors[index] = rowErrors;
      }
    });

    // Tiers must not overlap — overlapping ranges make tier selection ambiguous
    // and bill the wrong unit price.
    const ranges = forms
      .map((f, i) => ({ i, s: Number(f.StartRange), e: Number(f.EndRange) }))
      .filter((r) => Number.isFinite(r.s) && Number.isFinite(r.e) && r.e > r.s)
      .sort((a, b) => a.s - b.s);
    for (let k = 1; k < ranges.length; k++) {
      if (ranges[k].s <= ranges[k - 1].e) {
        errors[ranges[k].i] = {
          ...(errors[ranges[k].i] || {}),
          StartRange: true,
        };
        isValid = false;
        hasRangeIssue = true;
      }
    }

    if (Object.keys(errors).length) {
      toast.error(
        hasRangeIssue
          ? "Check unit ranges: end must be greater than start and tiers must not overlap"
          : "Add all required feilds",
      );
      setUnitPriceErrors(errors);
      return isValid;
    }

    // Exactly one row must be the Default — the marketplace card and billing
    // fall back to the Default row's price, so zero defaults breaks display
    // and multiple defaults picks one arbitrarily.
    const defaultCount = forms.filter(isDefaultRow).length;
    if (defaultCount !== 1) {
      toast.error(
        defaultCount === 0
          ? "Mark one unit price row as Default"
          : "Only one unit price row can be Default",
      );
      return false;
    }
    return isValid;
  };

  const markChangedUnits = (forms = unitPriceForms) => {
    // FIX 4: diff against the ORIGINAL DB snapshot, not the live `unitPrices`
    // prop (which mutates on every live-commit keystroke).
    const originals = originalUnitPricesRef.current || [];
    return forms.map((curr) => {
      const original = originals.find((u) => u.id === curr.id);
      // FIX 2: price fields now hold RAW STRINGS ("615." while typing 615.6),
      // so compare them numerically — a string "615.6" and DB number 615.6 must
      // NOT read as a change. Ranges are also compared numerically for the same
      // string-vs-number tolerance. hsn_code is a plain string code.
      const numChanged = (f) => Number(curr[f]) !== Number(original[f]);

      // compare between old and new unit_price
      const changed =
        !original ||
        numChanged("Marketplace_price") ||
        numChanged("Vendor_price") ||
        numChanged("StartRange") ||
        numChanged("EndRange") ||
        numChanged("Industry_price") ||
        // these fields were silently skipped, so edits to them never flagged
        // the row as changed and never reached the price-update pipeline
        numChanged("Min_price") ||
        numChanged("Max_price") ||
        curr.hsn_code !== original.hsn_code ||
        // DefaultValue may be boolean (edited) or "true"/"false" string (DB)
        isDefaultRow(curr) !== isDefaultRow(original);

      // return true if any changes in unit_price
      return {
        ...curr,
        changed_unitprice: changed,
      };
    });
  };

  // LIVE-COMMIT (inline mode): push the current tiers straight into the plan's
  // unit_ids on every edit so the outer "Save" always has the latest prices —
  // no separate "Apply unit prices" click required. We deliberately do NOT gate
  // this on validation: transient invalid states (e.g. a half-typed range) still
  // sync so nothing is silently dropped; validation errors are surfaced inline
  // and the outer Save runs its own gate. Existing rows keep their integer `id`
  // (so the backend UPDATEs the row) and the capitalized price keys the backend
  // syncUnits reads are preserved untouched by markChangedUnits.
  const commitInline = (forms) => {
    if (!inline) return;
    onSave(markChangedUnits(forms));
  };

  const handleSave = () => {
    const isValid = validateUnitPriceForms(unitPriceForms);
    if (!isValid) {
      return;
    }
    //Calculate changes in required field
    const updatedUnits = markChangedUnits();

    // Inline: edits are already committed live on change. This button is now a
    // validate/confirm affordance — re-sync the validated set (the hard
    // active-subscription confirm lives on the outer Save) and acknowledge.
    if (inline) {
      onSave(updatedUnits);
      toast.success("Unit prices applied");
      return;
    }

    const changedSomething = updatedUnits.some((u) => u.changed_unitprice);

    if (!allow_delete && changedSomething) {
      setShowConfirmation(true);
      return;
    } else {
      setShowCard(false);
      onSave(updatedUnits);
      return;
    }
  };

  const handleSaveFromConfirmation = () => {
    const updatedUnits = markChangedUnits();
    setShowConfirmation(false);
    setShowCard(false);
    onSave(updatedUnits);
    return;
  };

  const handleChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    const realValue =
      type === "checkbox"
        ? checked
        : type === "number"
          ? value === ""
            ? ""
            : Number(value)
          : value;
    let updatedForms;
    if (name === "DefaultValue") {
      updatedForms = unitPriceForms.map((form, i) =>
        i === index
          ? { ...form, DefaultValue: realValue }
          : { ...form, DefaultValue: false },
      );
    } else {
      updatedForms = unitPriceForms.map((form, i) =>
        i === index ? { ...form, [name]: realValue } : form,
      );
    }
    setUnitPriceForms(updatedForms);
    setUnitPriceErrors((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [name]: false,
      },
    }));
    commitInline(updatedForms);
  };

  const deleteHandler = (index) => {
    const updatedForms = unitPriceForms.filter((val, i) => i !== index);
    setUnitPriceForms(updatedForms);
    commitInline(updatedForms);
  };

  const Label = ({ children, infoKey }) => (
    <Form.Label className={styles.unitPriceLabel}>
      {children}
      <FieldInfo text={FIELD_INFO[infoKey]} />
    </Form.Label>
  );

  const bodyInner =
    unitPriceForms.length !== 0 ? (
      <>
        {unitPriceForms.map((form, index) => (
          <Form
            key={index}
            className={styles.card}
            onSubmit={(e) => e.preventDefault()}
          >
            <div className={styles.unitPriceTop}>
              <h1 className={styles.heading}>Tier {index + 1}</h1>
              {index !== 0 &&
              // FIX 3: the hydrate effect force-sets type:"existing" on every
              // row, so a just-added tier lost its Delete button on locked
              // plans. Gate on real row identity instead: a freshly-added row
              // carries a generated STRING id, an existing DB row an INTEGER id.
              (allow_delete || !Number.isInteger(form.id)) &&
              adminVendorId === productVendorId ? (
                <Button
                  variant="danger"
                  disabled={index === 0}
                  onClick={() => deleteHandler(index)}
                  className={styles.deleteButton}
                >
                  Delete
                </Button>
              ) : (
                ""
              )}
            </div>
            <div className={styles.cardBody}>
              <Row>
                <Col md={4}>
                  <Form.Group controlId={`formStartRange-${index}`}>
                    <Label infoKey="unit.start_range">Start Range</Label>
                    <Form.Control
                      required
                      type="number"
                      name="StartRange"
                      value={form.StartRange}
                      onChange={(e) => handleChange(index, e)}
                      onWheel={(e) => e.target.blur()}
                      placeholder="Start Range"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.StartRange
                          ? styles.inputError
                          : ""
                      }`}
                      readOnly={adminVendorId !== productVendorId}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId={`formEndRange-${index}`}>
                    <Label infoKey="unit.end_range">End Range</Label>
                    <Form.Control
                      required
                      type="number"
                      name="EndRange"
                      value={form.EndRange}
                      onChange={(e) => handleChange(index, e)}
                      onWheel={(e) => e.target.blur()}
                      placeholder="End Range"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.EndRange
                          ? styles.inputError
                          : ""
                      }`}
                      readOnly={adminVendorId !== productVendorId}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId={`formMarketplacePrice-${index}`}>
                    <Label infoKey="unit.marketplace_price">
                      Marketplace Price
                    </Label>
                    <Form.Control
                      required
                      type="text"
                      inputMode="decimal"
                      name="Marketplace_price"
                      value={form.Marketplace_price}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="Marketplace Price"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.Marketplace_price
                          ? styles.inputError
                          : ""
                      }`}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group controlId={`formIndustryPrice-${index}`}>
                    <Label infoKey="unit.industry_price">Industry Price</Label>
                    <Form.Control
                      required
                      type="text"
                      inputMode="decimal"
                      name="Industry_price"
                      value={form.Industry_price}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="Industry Price"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.Industry_price
                          ? styles.inputError
                          : ""
                      }`}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId={`formVendorPrice-${index}`}>
                    <Label infoKey="unit.vendor_price">Vendor Price</Label>
                    <Form.Control
                      required
                      type="text"
                      inputMode="decimal"
                      name="Vendor_price"
                      value={form.Vendor_price}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="Vendor Price"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.Vendor_price
                          ? styles.inputError
                          : ""
                      }`}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId={`formMinPrice-${index}`}>
                    <Label infoKey="unit.min_price">Min Price</Label>
                    <Form.Control
                      required
                      type="text"
                      inputMode="decimal"
                      name="Min_price"
                      value={form.Min_price}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="Min Price"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.Min_price
                          ? styles.inputError
                          : ""
                      }`}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group controlId={`formMaxPrice-${index}`}>
                    <Label infoKey="unit.max_price">Max Price</Label>
                    <Form.Control
                      required
                      type="text"
                      inputMode="decimal"
                      name="Max_price"
                      value={form.Max_price}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="Max Price"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.Max_price
                          ? styles.inputError
                          : ""
                      }`}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId={`formHsn-${index}`}>
                    <Label infoKey="unit.hsn_code">HSN Code</Label>
                    <Form.Control
                      required
                      type="string"
                      name="hsn_code"
                      value={form.hsn_code}
                      onChange={(e) => handleChange(index, e)}
                      placeholder="HSN Code"
                      className={`${styles.formControl} ${
                        unitPriceErrors[index]?.hsn_code
                          ? styles.inputError
                          : ""
                      }`}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId={`formDefault-${index}`}>
                    <Form.Label
                      style={{ display: "block" }}
                      className={styles.unitPriceLabel}
                    >
                      {" "}
                      Default:
                      <FieldInfo text={FIELD_INFO["unit.default"]} />
                    </Form.Label>
                    <ButtonGroup>
                      <button
                        type="button"
                        className={
                          isDefaultRow(form)
                            ? styles.primary
                            : styles.outlinePrimary
                        }
                        onClick={() =>
                          handleChange(index, {
                            target: {
                              name: "DefaultValue",
                              type: "checkbox",
                              checked: true,
                            },
                          })
                        }
                      >
                        True
                      </button>
                      <button
                        type="button"
                        className={
                          !isDefaultRow(form)
                            ? styles.danger
                            : styles.outlineDanger
                        }
                        onClick={() =>
                          handleChange(index, {
                            target: {
                              name: "DefaultValue",
                              type: "checkbox",
                              checked: false,
                            },
                          })
                        }
                      >
                        False
                      </button>
                    </ButtonGroup>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Form>
        ))}
        {adminVendorId === productVendorId && (
          <Button className={styles.addPriceUnit} onClick={addNewForm}>
            + Add tier
          </Button>
        )}
        {/* Inline (admin) mode live-commits every edit into the plan, so a
            separate "Apply unit prices" click did nothing and only confused —
            removed. The outer Save validates. The button remains for the
            non-inline popup editor, where it IS the commit action. */}
        {!inline && (
          <Button className={styles.submitBtn} onClick={handleSave}>
            Submit
          </Button>
        )}
      </>
    ) : (
      <p>No Unit Price available</p>
    );

  return (
    <div className={styles.container}>
      {!inline && (
        <button
          className={styles.EditUnitPrice}
          onClick={(e) => {
            e.preventDefault();
            setShowCard(true);
          }}
          aria-haspopup="dialog"
          aria-expanded={showCard}
        >
          <FaEdit />
        </button>
      )}
      {inline ? (
        <div className={styles.inlineUnitBody}>
          {!allow_delete && (
            <div className={styles.activeSubWarning}>
              <span aria-hidden="true">⚠</span>
              <span style={{ flex: 1 }}>
                This plan has active subscriptions — changing these prices will
                re-price existing customers at their next renewal.
              </span>
              <ActiveSubscriptionsBadge priceId={priceId} />
            </div>
          )}
          {bodyInner}
        </div>
      ) : (
        showCard && (
          <div className={styles.unitPriceContainer}>
            <div
              className={styles.dropShadow}
              onClick={() => setShowCard(false)}
              aria-hidden="true"
            />
            <div
              className={styles.unitPricefield}
              role="dialog"
              aria-modal="true"
              aria-label="Unit Prices"
              onKeyDown={(e) => {
                if (e.key === "Escape") setShowCard(false);
              }}
            >
              <div className={styles.unitBody}>{bodyInner}</div>
            </div>
          </div>
        )
      )}
      {showConfirmation && (
        <UnitPriceConfirmation
          onClose={() => setShowConfirmation(false)}
          onSave={() => {
            handleSaveFromConfirmation();
          }}
        />
      )}
    </div>
  );
};

export default UnitPriceForm;
